// @greenlight-spec: 41-groups-management.md
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Lock, Plus, Search, X } from 'lucide-react';

import { GLDrawer } from '@/components/greenlight/GLDrawer';
import { UserAvatar } from '@/components/greenlight/UserAvatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { thrownCode, thrownMessage } from '@/lib/apiErrors';
import { deriveListQuery } from '@/lib/listQuery';
import type { GroupMember, UserListItem } from '@greenlight/shared';

import {
  candidateMembers,
  shownMemberCountLabel,
  SOURCE_LABEL,
  type GroupRow,
} from './groups-logic';

const PAGE_SIZE = 100;

export interface GroupDetailProps {
  row: GroupRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** org_admin — without it the member list renders, but nothing mutates. */
  canManage: boolean;
  /** The user page already loaded by /users, used for the picker and IdP membership. */
  users: UserListItem[];
  onChanged: () => void;
}

/**
 * Membership view for one group. A Greenlight group's members come from the
 * API and are editable here; a registered directory group's are whatever login
 * has synced onto `users.idp_groups`, shown read-only because the directory is
 * authoritative (docs/41 §Registered IdP groups).
 */
export function GroupDetail({
  row,
  open,
  onOpenChange,
  canManage,
  users,
  onChanged,
}: GroupDetailProps): React.JSX.Element {
  const queryClient = useQueryClient();
  const [query, setQuery] = React.useState('');
  const [busyUserId, setBusyUserId] = React.useState<string | null>(null);

  const isLocal = row?.kind === 'greenlight';
  const membersQuery = api.groups.listMembers.useQuery(
    ['group-members', row?.id ?? 'none'],
    { params: { id: row?.id ?? '' }, query: { limit: PAGE_SIZE } },
    { queryKey: ['group-members', row?.id ?? 'none'], enabled: open && isLocal && row !== null },
  );
  const {
    items: fetchedMembers,
    nextCursor,
    hasError,
  } = deriveListQuery<GroupMember>({
    data: membersQuery.data,
    isError: membersQuery.isError,
  });

  const addMember = api.groups.addMember.useMutation();
  const removeMember = api.groups.removeMember.useMutation();

  // A directory group's membership is not stored per group — it is the set of
  // users whose last login carried the identifier.
  const syncedMembers = React.useMemo<GroupMember[]>(() => {
    if (row === null || row.kind !== 'idp') return [];
    return users
      .filter((u) => (u.groups ?? []).some((g) => g.source === 'idp' && g.id === row.identifier))
      .map((u) => ({
        id: u.id,
        email: u.email,
        display_name: u.display_name,
        role: u.role,
        is_active: u.is_active,
        added_at: u.created_at,
      }));
  }, [row, users]);

  const members = isLocal ? fetchedMembers : syncedMembers;
  const candidates = React.useMemo(
    () => (isLocal ? candidateMembers(users, members, query) : []),
    [isLocal, users, members, query],
  );

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['group-members', row?.id ?? 'none'] });
    onChanged();
  };

  const handleAdd = async (user: UserListItem): Promise<void> => {
    if (row === null) return;
    setBusyUserId(user.id);
    try {
      const res = await addMember.mutateAsync({
        params: { id: row.id },
        body: { user_id: user.id },
      });
      if (res.status === 201) {
        toast.success(`Added ${user.display_name ?? user.email} to ${row.name}.`);
        setQuery('');
        refresh();
      } else {
        toast.error("Couldn't add that member.");
      }
    } catch (err) {
      // Reachable when the group runs past the page of members loaded here: the
      // picker cannot tell that someone it is offering is already in.
      if (thrownCode(err) === 'groups.member_exists') {
        toast.error(`${user.display_name ?? user.email} is already a member of ${row.name}.`);
      } else {
        toast.error(thrownMessage(err, "Couldn't add that member."));
      }
    } finally {
      setBusyUserId(null);
    }
  };

  const handleRemove = async (member: GroupMember): Promise<void> => {
    if (row === null) return;
    setBusyUserId(member.id);
    try {
      const res = await removeMember.mutateAsync({
        params: { id: row.id, userId: member.id },
      });
      if (res.status === 204) {
        toast.success(`Removed ${member.display_name ?? member.email} from ${row.name}.`);
        refresh();
      } else {
        toast.error("Couldn't remove that member.");
      }
    } catch (err) {
      toast.error(thrownMessage(err, "Couldn't remove that member."));
    } finally {
      setBusyUserId(null);
    }
  };

  // The header labels the list below it, never a figure the list contradicts.
  // Withheld until the list settles — a non-200 is not "0 members" (docs/28
  // §/users States).
  const countSettled = !isLocal || (!membersQuery.isLoading && !hasError);
  // A directory group's list is short by design — bounded by the roster page.
  // A Greenlight group's is short only when the server says another page
  // follows, so its own response settles it rather than a count from the list
  // that may have moved since.
  const mayHoldMore = row !== null && (row.kind === 'idp' ? true : nextCursor !== null);
  const knownTotal = row !== null && mayHoldMore ? row.memberCount : members.length;
  const unlistedSynced = row !== null && row.kind === 'idp' ? knownTotal - members.length : 0;
  const headerNode = row ? (
    <div className="min-w-0">
      <div className="text-[17px] font-bold tracking-tight text-foreground truncate">
        {row.name}
      </div>
      <div className="mt-0.5 text-[12.5px] text-muted-foreground truncate">
        {SOURCE_LABEL[row.kind]}
        {countSettled ? ` · ${shownMemberCountLabel(row.kind, members.length, knownTotal)}` : ''}
      </div>
    </div>
  ) : null;

  return (
    <GLDrawer
      open={open}
      onOpenChange={onOpenChange}
      variant="panel"
      title={headerNode ?? ''}
      srDescription={row ? `Members of ${row.name}` : 'Group members'}
      storageKey="gl-groups-panel-width"
      mobileBreakpoint={768}
    >
      {row === null ? null : (
        <div className="space-y-5" data-testid="group-detail">
          {row.description !== null && row.description !== '' && (
            <p className="text-[12.5px] text-muted-foreground">{row.description}</p>
          )}

          {row.kind === 'idp' && (
            <>
              <div className="rounded-gl border border-border bg-card px-4 py-3">
                <div className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-muted-foreground">
                  Identifier
                </div>
                <div className="mt-1 font-mono text-[12px] break-all text-foreground">
                  {row.identifier}
                </div>
              </div>
              <p className="flex items-start gap-1.5 text-[12px] text-muted-foreground">
                <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span>
                  Membership is managed in your identity provider. This list covers people who have
                  signed in to Greenlight since joining the group — it is not the directory&apos;s
                  full answer.
                  {unlistedSynced > 0 && (
                    <>
                      {' '}
                      <strong className="font-medium text-foreground">
                        {unlistedSynced} more {unlistedSynced === 1 ? 'member is' : 'members are'}{' '}
                        on another page of the roster
                      </strong>{' '}
                      — page through Users above to see them.
                    </>
                  )}
                </span>
              </p>
            </>
          )}

          <section className="space-y-2.5">
            <h3 className="ml-0.5 font-mono text-[10.5px] uppercase tracking-[0.08em] font-medium text-foreground">
              Members
            </h3>

            {isLocal && membersQuery.isLoading ? (
              <div className="space-y-2" data-testid="group-members-loading">
                <Skeleton className="h-12 w-full rounded-gl" />
                <Skeleton className="h-12 w-full rounded-gl" />
              </div>
            ) : isLocal && hasError ? (
              <p className="text-[12.5px] text-destructive" data-testid="group-members-error">
                Couldn&apos;t load this group&apos;s members. Retry in a moment.
              </p>
            ) : members.length === 0 ? (
              <p className="text-[12.5px] text-muted-foreground">
                {row.kind !== 'idp'
                  ? 'No members yet — an empty group admits nobody extra to the apps it is bound to.'
                  : unlistedSynced > 0
                    ? 'None of this group’s members are on the page of the roster loaded above — page through Users to reach them.'
                    : 'Nobody in this group has signed in to Greenlight yet.'}
              </p>
            ) : (
              <ul className="overflow-hidden rounded-gl-lg border border-border bg-card divide-y divide-border">
                {members.map((m) => (
                  <li key={m.id} className="flex items-center gap-3 px-4 py-2.5">
                    <UserAvatar
                      name={m.display_name ?? m.email}
                      size={28}
                      deactivated={!m.is_active}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[12.5px] text-foreground">
                        {m.display_name ?? m.email}
                      </div>
                      {m.display_name !== null && (
                        <div className="truncate text-[11.5px] text-muted-foreground">
                          {m.email}
                        </div>
                      )}
                    </div>
                    {isLocal && canManage && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-11 w-11 md:h-7 md:w-7"
                        aria-label={`Remove ${m.display_name ?? m.email} from ${row.name}`}
                        disabled={busyUserId === m.id}
                        onClick={() => void handleRemove(m)}
                      >
                        <X className="h-3.5 w-3.5" aria-hidden="true" />
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {isLocal && countSettled && knownTotal > members.length && (
              <p
                className="text-[12px] text-muted-foreground"
                data-testid="group-members-truncated"
              >
                {knownTotal - members.length} more{' '}
                {knownTotal - members.length === 1 ? 'member is' : 'members are'} in this group —
                only the first page loads here.
              </p>
            )}
          </section>

          {isLocal && canManage && (
            <section className="space-y-2.5">
              <h3 className="ml-0.5 font-mono text-[10.5px] uppercase tracking-[0.08em] font-medium text-foreground">
                Add a member
              </h3>
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
                  aria-hidden="true"
                />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search name or email…"
                  aria-label="Search users to add"
                  className="h-11 md:h-9 pl-8"
                />
              </div>
              {candidates.length === 0 ? (
                <p className="text-[12px] text-muted-foreground">
                  No matching users on this page of the roster.
                </p>
              ) : (
                <ul className="overflow-hidden rounded-gl-lg border border-border bg-card divide-y divide-border">
                  {candidates.slice(0, 8).map((u) => (
                    <li key={u.id} className="flex items-center gap-3 px-4 py-2.5">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[12.5px] text-foreground">
                          {u.display_name ?? u.email}
                        </div>
                        {u.display_name !== null && (
                          <div className="truncate text-[11.5px] text-muted-foreground">
                            {u.email}
                          </div>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-11 md:h-8"
                        disabled={busyUserId === u.id}
                        onClick={() => void handleAdd(u)}
                      >
                        <Plus className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                        Add
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}
        </div>
      )}
    </GLDrawer>
  );
}
